import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import './Landing.css';

/* ─── Animated counter ─────────────────────────────────────────────────────── */
function CountUp({ end, prefix = '', suffix = '', duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  useEffect(() => {
    if (!inView) return;
    const start = Date.now();
    const tick = () => {
      const p = Math.min(1, (Date.now() - start) / duration);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(ease * end));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, end, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

/* ─── Fade-in on scroll ────────────────────────────────────────────────────── */
function FadeIn({ children, delay = 0, direction = 'up', className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const variants = {
    hidden: {
      opacity: 0,
      y: direction === 'up' ? 30 : direction === 'down' ? -30 : 0,
      x: direction === 'left' ? 30 : direction === 'right' ? -30 : 0,
    },
    visible: { opacity: 1, y: 0, x: 0 },
  };
  return (
    <motion.div
      ref={ref}
      className={className}
      variants={variants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Navbar ───────────────────────────────────────────────────────────────── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <nav className={`av-nav ${scrolled ? 'nav-solid' : ''}`}>
      <div className="nav-inner">
        <Link to="/" className="nav-logo">
          <span className="nav-logo-text">Alpha<span>Vault</span></span>
        </Link>
        <div className="nav-links desktop-only">
          <a href="#how">How It Works</a>
          <Link to="/pricing">Pricing</Link>
          <a href="#platforms">Platforms</a>
          <a href="#faq">FAQ</a>
          <Link to="/bots" className="nav-bots-link">🤖 AI Bots</Link>
        </div>
        <div className="nav-actions desktop-only">
          <Link to="/login" className="nav-login">Login</Link>
          <Link to="/register" className="btn btn-primary btn-sm">Start Challenge</Link>
        </div>
        <button className="nav-burger mobile-only" onClick={() => setMenuOpen(!menuOpen)}>
          <span /><span /><span />
        </button>
      </div>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="nav-mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <a href="#how" onClick={() => setMenuOpen(false)}>How It Works</a>
            <Link to="/pricing" onClick={() => setMenuOpen(false)}>Pricing</Link>
            <a href="#platforms" onClick={() => setMenuOpen(false)}>Platforms</a>
            <a href="#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
            <Link to="/bots" className="nav-bots-link" onClick={() => setMenuOpen(false)}>🤖 AI Bots</Link>
            <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
            <Link to="/register" className="btn btn-primary" onClick={() => setMenuOpen(false)}>Start Challenge</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

/* ─── Payout ticker ────────────────────────────────────────────────────────── */
const PAYOUTS = [
  { user: 'K***n', amount: 1840, tag: 'BTC · $100K' },
  { user: 'A***a', amount: 3210, tag: 'ETH · $200K' },
  { user: 'M***e', amount: 740,  tag: 'SOL · $25K'  },
  { user: 'J***s', amount: 5600, tag: 'BTC · $200K' },
  { user: 'R***z', amount: 920,  tag: 'BNB · $50K'  },
  { user: 'T***o', amount: 2100, tag: 'ETH · $100K' },
  { user: 'D***i', amount: 4350, tag: 'BTC · $200K' },
];
function PayoutTicker() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % PAYOUTS.length), 3500);
    return () => clearInterval(id);
  }, []);
  const p = PAYOUTS[idx];
  return (
    <div className="ticker-bar">
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          className="ticker-inner"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
        >
          <span className="ticker-pulse" />
          <strong>{p.user}</strong> just received a payout —{' '}
          <span className="ticker-amount">${p.amount.toLocaleString()} USDT</span>
          <span className="ticker-tag">{p.tag}</span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ─── Hero ─────────────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="hero-section">
      <div className="hero-glow-1" />
      <div className="hero-glow-2" />
      <div className="hero-content">
        <motion.div
          className="hero-badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <span className="hero-badge-dot" />
          Crypto Prop Trading · Up to $200K Funded
        </motion.div>
        <motion.h1
          className="hero-h1"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          Get Funded.<br />
          Trade With Capital.<br />
          <span className="hero-gradient-text">Keep Up to 90%.</span>
        </motion.h1>
        <motion.p
          className="hero-sub"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          Prove your edge with an AlphaVault challenge. Pass the evaluation,
          receive a funded account, and earn up to 90% of your profits —
          paid in USDT within 24 hours.
        </motion.p>
        <motion.div
          className="hero-btns"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.48 }}
        >
          <Link to="/register" className="btn btn-primary btn-lg hero-cta">
            Start Challenge →
          </Link>
          <Link to="/register" className="btn-ghost-hero">
            Free Trial — No Payment
          </Link>
        </motion.div>
        <motion.div
          className="hero-trust"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.65 }}
        >
          <div className="ht-item">
            <span className="ht-stars">★★★★★</span>
            <span className="ht-label">4.9 / 5 Rating</span>
          </div>
          <div className="ht-div" />
          <div className="ht-item">
            <span className="ht-val">12,400+</span>
            <span className="ht-label">Traders Funded</span>
          </div>
          <div className="ht-div" />
          <div className="ht-item">
            <span className="ht-val">$3.8M+</span>
            <span className="ht-label">Total Paid Out</span>
          </div>
          <div className="ht-div" />
          <div className="ht-item">
            <span className="ht-val">24hr</span>
            <span className="ht-label">Payout Speed</span>
          </div>
        </motion.div>
      </div>

      {/* Floating dashboard card */}
      <motion.div
        className="hero-card-wrap"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="hero-dash-card">
          <div className="hdc-top">
            <div className="hdc-status">
              <span className="hdc-dot" />
              Live Account
            </div>
            <span className="hdc-plan-tag">$100K · 2-Step</span>
          </div>
          <div className="hdc-balance">$107,842.50</div>
          <div className="hdc-pnl">+$7,842.50 <span className="hdc-pct">+7.84%</span></div>
          <HeroSparkline />
          <div className="hdc-rules">
            {[
              { label: 'Profit Target', val: '78%', pct: 78, color: '#10b981' },
              { label: 'Trading Days 8/10', val: '80%', pct: 80, color: '#7c3aed' },
              { label: 'Daily Loss Used', val: '24%', pct: 24, color: '#f59e0b' },
            ].map(r => (
              <div key={r.label} className="hdc-rule">
                <div className="hdc-rule-header">
                  <span>{r.label}</span><span style={{ color: r.color }}>{r.val}</span>
                </div>
                <div className="hdc-bar-track">
                  <motion.div
                    className="hdc-bar-fill"
                    style={{ background: r.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${r.pct}%` }}
                    transition={{ duration: 1, delay: 0.8 + Math.random() * 0.3, ease: 'easeOut' }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="hdc-footer">
            <span className="hdc-live-dot" />
            Live · BTC/USDT · Bybit
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function HeroSparkline() {
  const data = [100, 101.2, 100.8, 102.4, 103.1, 102.7, 104.5, 105.9, 105.2, 107.1, 106.8, 107.8];
  const w = 300, h = 60;
  const min = Math.min(...data), max = Math.max(...data);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / (max - min)) * (h - 8) - 4;
    return `${x},${y}`;
  });
  const line = `M ${pts.join(' L ')}`;
  const fill = `${line} L ${w},${h} L 0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="hero-sparkline" preserveAspectRatio="none">
      <defs>
        <linearGradient id="heroGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} fill="url(#heroGrad)" />
      <path d={line} fill="none" stroke="#10b981" strokeWidth="2" />
    </svg>
  );
}

/* ─── Stats bar ────────────────────────────────────────────────────────────── */
function StatsBar() {
  return (
    <div className="stats-bar">
      {[
        { end: 12400,   prefix: '',  suffix: '+', label: 'Traders Funded'   },
        { end: 3800000, prefix: '$', suffix: '',  label: 'Total Paid Out'   },
        { end: 98,      prefix: '',  suffix: '%', label: 'Payout Approval'  },
        { end: 200000,  prefix: '$', suffix: '',  label: 'Max Account Size' },
      ].map(s => (
        <div key={s.label} className="stat-item">
          <span className="stat-val"><CountUp {...s} /></span>
          <span className="stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── How it works ─────────────────────────────────────────────────────────── */
function HowItWorks() {
  const steps = [
    { n: '01', icon: '🎯', title: 'Buy a Challenge',    desc: 'Choose your account size from $10K to $200K. Pay a one-time fee and start trading immediately.' },
    { n: '02', icon: '📈', title: 'Pass Evaluation',    desc: 'Hit your profit target while staying within drawdown limits. Complete the minimum trading days.' },
    { n: '03', icon: '✅', title: 'Get Funded',         desc: 'Pass all phases and receive your funded trading account with full access to live markets.' },
    { n: '04', icon: '💸', title: 'Earn Profits',       desc: 'Request USDT payouts within 12–24 hours. Earn up to 90% profit split as you scale.' },
  ];
  return (
    <section className="hiw-section" id="how">
      <FadeIn>
        <div className="section-eyebrow">Process</div>
        <h2 className="section-h2">How It Works</h2>
        <p className="section-sub">Simple, transparent, fair — designed for serious crypto traders.</p>
      </FadeIn>
      <div className="hiw-steps">
        {steps.map((s, i) => (
          <FadeIn key={s.n} delay={i * 0.12}>
            <div className="hiw-step">
              <div className="hiw-step-num">{s.n}</div>
              <div className="hiw-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
            {i < steps.length - 1 && <div className="hiw-connector" />}
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

/* ─── Pricing ──────────────────────────────────────────────────────────────── */
function PricingSection() {
  const [type, setType] = useState('2-step');
  const plans = [
    { size: '$5K',   price1: 59,  price2: 44,  popular: false },
    { size: '$10K',  price1: 109, price2: 79,  popular: false },
    { size: '$25K',  price1: 199, price2: 149, popular: false },
    { size: '$50K',  price1: 269, price2: 199, popular: false },
    { size: '$100K', price1: 399, price2: 299, popular: true  },
    { size: '$200K', price1: 699, price2: 499, popular: false },
  ];
  return (
    <section className="pricing-section" id="pricing">
      <FadeIn>
        <div className="section-eyebrow">Plans</div>
        <h2 className="section-h2">Choose Your Challenge</h2>
        <p className="section-sub">Select the account size that fits your trading style. Fee refunded on first payout.</p>
      </FadeIn>
      <FadeIn delay={0.1}>
        <div className="pricing-toggle">
          {['1-step', '2-step'].map(t => (
            <button
              key={t}
              className={`ptoggle-btn ${type === t ? 'active' : ''}`}
              onClick={() => setType(t)}
            >
              {t === '2-step' && <span className="ptoggle-recommended">Recommended</span>}
              {t === '1-step' ? '1-Step Challenge' : '2-Step Challenge'}
            </button>
          ))}
        </div>
      </FadeIn>
      <div className="pricing-cards">
        {plans.map((p, i) => (
          <FadeIn key={p.size} delay={i * 0.08}>
            <div className={`pricing-card ${p.popular ? 'pricing-popular' : ''}`}>
              {p.popular && <div className="pop-badge">Most Popular</div>}
              <div className="pc-size">{p.size}</div>
              <div className="pc-price">
                <span className="pc-dollar">$</span>
                <span className="pc-amount">{type === '1-step' ? p.price1 : p.price2}</span>
                <span className="pc-note">one-time</span>
              </div>
              <div className="pc-divider" />
              <ul className="pc-features">
                {[
                  `${p.size} Funded Account`,
                  type === '1-step' ? '10% Profit Target' : '10% + 5% Targets',
                  type === '1-step' ? '6% Max Drawdown' : '10% Max Drawdown',
                  '5% Daily Loss Limit',
                  'Min 10 Trading Days',
                  '70% → 90% Split',
                  'Fee Refunded on Payout',
                  'USDT / USDC Payouts',
                ].map(f => (
                  <li key={f}><span className="pc-check">✓</span>{f}</li>
                ))}
              </ul>
              <Link to="/pricing" className={`pc-cta ${p.popular ? 'btn btn-primary' : 'btn btn-outline'}`}>
                Start Challenge →
              </Link>
            </div>
          </FadeIn>
        ))}
      </div>
      <FadeIn delay={0.2}>
        <p className="pricing-note">
          All plans include fee refund on first payout · USDT payouts in 12–24 hours · Scale up to 10×
        </p>
      </FadeIn>
    </section>
  );
}

/* ─── Features ─────────────────────────────────────────────────────────────── */
function Features() {
  const feats = [
    { icon: '⚡', title: '700+ Trading Pairs',      desc: 'Trade BTC, ETH, SOL and hundreds of crypto pairs.' },
    { icon: '📊', title: 'Up to 1:100 Leverage',    desc: 'Professional-grade leverage across spot and derivatives.' },
    { icon: '💸', title: '12–24 Hr Payouts',        desc: 'Fast USDT/USDC withdrawals processed same day.' },
    { icon: '🏆', title: '70% → 90% Split',         desc: 'Profit share grows with every successful payout cycle.' },
    { icon: '🔄', title: 'Fee Refunded',             desc: 'Challenge fee fully refunded with your first payout.' },
    { icon: '📈', title: 'Scale to $1M+',            desc: 'Proven traders can grow funded accounts up to 10×.' },
    { icon: '🤖', title: 'Algo-Friendly',            desc: 'Bots, EAs, and automated strategies fully supported.' },
    { icon: '🛡',  title: 'No Hidden Rules',         desc: 'All rules stated upfront. Full transparency guaranteed.' },
  ];
  return (
    <section className="features-section">
      <FadeIn>
        <div className="section-eyebrow">Features</div>
        <h2 className="section-h2">Built for Serious Traders</h2>
        <p className="section-sub">Everything you need to trade at a professional level.</p>
      </FadeIn>
      <div className="features-grid">
        {feats.map((f, i) => (
          <FadeIn key={f.title} delay={i * 0.06}>
            <motion.div
              className="feat-card"
              whileHover={{ y: -4, borderColor: 'rgba(124,58,237,0.5)' }}
              transition={{ duration: 0.2 }}
            >
              <div className="feat-icon">{f.icon}</div>
              <h4>{f.title}</h4>
              <p>{f.desc}</p>
            </motion.div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

/* ─── Exchange logo components ─────────────────────────────────────────────── */
const EXCHANGES = [
  { id: 'bybit',    name: 'Bybit',     abbr: 'BY',  bg: '#1a1a2e', accent: '#f7a600', feats: ['700+ crypto pairs', 'Up to 1:100 leverage', 'Spot & Perpetual Futures', 'Sub-10ms execution', 'USDT-M & Coin-M contracts'] },
  { id: 'binance',  name: 'Binance',   abbr: 'BNB', bg: '#0b0e11', accent: '#f3ba2f', feats: ['350+ trading pairs', 'Industry-leading liquidity', 'Futures & Options', 'Copy trading support', 'Advanced order types'] },
  { id: 'coinbase', name: 'Coinbase',  abbr: 'CB',  bg: '#0052ff', accent: '#fff',    feats: ['Regulated US exchange', 'Spot & Advanced trading', 'Institutional grade', 'Simple & Pro interface', 'Wide fiat on-ramp'] },
  { id: 'okx',      name: 'OKX',       abbr: 'OKX', bg: '#121212', accent: '#e6ff00', feats: ['400+ trading pairs', 'DEX & DeFi integration', 'Copy trading', 'Perpetuals & Options', 'Web3 wallet built-in'] },
  { id: 'kraken',   name: 'Kraken',    abbr: 'KR',  bg: '#5741d9', accent: '#fff',    feats: ['Highly regulated', 'Spot, margin & futures', 'Industry-leading security', 'Dark pool orders', 'OTC desk available'] },
  { id: 'kucoin',   name: 'KuCoin',    abbr: 'KC',  bg: '#0a0a0a', accent: '#24ae8f', feats: ['700+ altcoins', 'Futures & margin trading', 'Bot marketplace', 'Lending & staking', 'P2P trading'] },
  { id: 'gate',     name: 'Gate.io',   abbr: 'GT',  bg: '#0e1420', accent: '#2354e6', feats: ['1,400+ trading pairs', 'Widest altcoin selection', 'Futures & ETF products', 'Quantitative trading', 'Copy trading'] },
  { id: 'bitget',   name: 'Bitget',    abbr: 'BG',  bg: '#0a0f1e', accent: '#00d9a6', feats: ['600+ crypto pairs', '#1 Copy Trading platform', 'One-click copy bots', 'Futures & Spot', 'USDT perpetuals'] },
  { id: 'mexc',     name: 'MEXC',      abbr: 'MX',  bg: '#050b14', accent: '#1ac9ff', feats: ['1,500+ token listings', 'Zero maker fees', 'Futures up to 200×', 'M-Day early access', 'KYC-lite trading'] },
  { id: 'htx',      name: 'HTX',       abbr: 'HTX', bg: '#132337', accent: '#2dacf7', feats: ['Formerly Huobi Global', '800+ trading pairs', 'Spot, futures & options', 'Institutional services', 'DM & Prime Broker'] },
  { id: 'bitfinex', name: 'Bitfinex',  abbr: 'BFX', bg: '#0a1628', accent: '#16b157', feats: ['Advanced trading tools', 'Deep order book', 'Margin & lending', 'OTC desk', 'Funding market'] },
  { id: 'api',      name: 'API / EA',  abbr: 'API', bg: '#1a0a2e', accent: '#a78bfa', feats: ['REST & WebSocket API', 'Bot & EA friendly', 'No strategy restrictions', 'Full trade history', 'Webhook support'] },
];

/* ─── Platform preview ─────────────────────────────────────────────────────── */
function PlatformPreview() {
  const [active, setActive] = useState('bybit');
  const ex = EXCHANGES.find(e => e.id === active) || EXCHANGES[0];

  return (
    <section className="platform-section" id="platforms">
      <FadeIn>
        <div className="section-eyebrow">Integrations</div>
        <h2 className="section-h2">Trade on Any Platform</h2>
        <p className="section-sub">AlphaVault connects to all major exchanges. Choose your preferred trading environment.</p>
      </FadeIn>

      {/* Exchange logo grid */}
      <FadeIn delay={0.1}>
        <div className="exchange-grid">
          {EXCHANGES.map((e, i) => (
            <motion.button
              key={e.id}
              className={`exchange-card ${active === e.id ? 'active' : ''}`}
              onClick={() => setActive(e.id)}
              whileHover={{ y: -3, scale: 1.03 }}
              transition={{ duration: 0.15 }}
              style={{ '--ex-accent': e.accent }}
            >
              <div className="ex-logo" style={{ background: e.bg, border: `1px solid ${e.accent}22` }}>
                <span className="ex-abbr" style={{ color: e.accent }}>{e.abbr}</span>
              </div>
              <span className="ex-name">{e.name}</span>
              {active === e.id && <span className="ex-active-dot" style={{ background: e.accent }} />}
            </motion.button>
          ))}
        </div>
      </FadeIn>

      {/* Selected exchange detail */}
      <div className="platform-inner">
        <FadeIn direction="right" className="platform-left">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              className="plat-detail"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.22 }}
            >
              <div className="plat-detail-header">
                <div className="plat-logo-lg" style={{ background: ex.bg, border: `1px solid ${ex.accent}33` }}>
                  <span style={{ color: ex.accent, fontWeight: 900, fontSize: 16 }}>{ex.abbr}</span>
                </div>
                <div>
                  <h3 style={{ color: '#f1f5f9', margin: 0 }}>{ex.name}</h3>
                  <span className="plat-verified">✓ Verified Compatible</span>
                </div>
              </div>
              <ul className="plat-feats">
                {ex.feats.map(f => (
                  <li key={f}>
                    <span className="plat-check" style={{ color: ex.accent }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <div className="plat-cta-row">
                <span className="plat-note">Full support · No restrictions · All order types</span>
              </div>
            </motion.div>
          </AnimatePresence>
        </FadeIn>

        <FadeIn direction="left" className="platform-right">
          <div className="terminal-card">
            <div className="term-titlebar">
              <span className="term-dot red" /><span className="term-dot yellow" /><span className="term-dot green" />
              <span className="term-title">alphavault · terminal</span>
            </div>
            <div className="term-pair">
              BTC / USDT
              <span className="term-price">$67,420.50 <span style={{color:'#10b981'}}>▲ +2.4%</span></span>
            </div>
            <TerminalChart />
            <div className="term-positions">
              <div className="term-pos-header">Open Positions</div>
              <div className="term-pos green">
                <span>BTC Long</span><span>0.15 BTC</span><span>+$312.40</span>
              </div>
              <div className="term-pos red">
                <span>ETH Short</span><span>2.5 ETH</span><span>-$48.20</span>
              </div>
            </div>
            <div className="term-metrics">
              <div><span>Balance</span><span className="tm-val purple">$107,264</span></div>
              <div><span>Drawdown</span><span className="tm-val yellow">2.14%</span></div>
              <div><span>P&L Today</span><span className="tm-val green">+$1,840</span></div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function TerminalChart() {
  const data = [67100, 67250, 67080, 67400, 67380, 67600, 67420, 67580, 67650, 67420, 67580, 67420];
  const w = 300, h = 80;
  const min = Math.min(...data), max = Math.max(...data);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / (max - min)) * (h - 10) - 5;
    return `${x},${y}`;
  });
  const line = `M ${pts.join(' L ')}`;
  const fill = `${line} L ${w},${h} L 0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="term-chart" preserveAspectRatio="none">
      <defs>
        <linearGradient id="termGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} fill="url(#termGrad)" />
      <path d={line} fill="none" stroke="#7c3aed" strokeWidth="2" />
    </svg>
  );
}

/* ─── Payout tiers ─────────────────────────────────────────────────────────── */
function PayoutTiers() {
  return (
    <section className="tiers-section">
      <div className="tiers-inner">
        <FadeIn direction="right" className="tiers-left">
          <div className="section-eyebrow">Profit Splits</div>
          <h2 className="section-h2">Earn More, Keep More</h2>
          <p className="section-sub">Your split grows with every successful payout cycle.</p>
          <div className="tier-cards">
            {[
              { tier: 'Tier 1', pct: '70%', req: 'From Day 1',       color: '#94a3b8' },
              { tier: 'Tier 2', pct: '80%', req: 'After 3 Payouts',  color: '#7c3aed' },
              { tier: 'Tier 3', pct: '90%', req: 'After 6 Payouts',  color: '#10b981' },
            ].map(t => (
              <motion.div
                key={t.tier}
                className="tier-card"
                whileHover={{ x: 6 }}
                transition={{ duration: 0.2 }}
              >
                <span className="tier-pct" style={{ color: t.color }}>{t.pct}</span>
                <div>
                  <div className="tier-name">{t.tier}</div>
                  <div className="tier-req">{t.req}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </FadeIn>
        <FadeIn direction="left" className="tiers-right">
          <div className="scale-card">
            <div className="scale-title">Scaling Plan</div>
            {[
              ['$10K',  5],  ['$25K', 14], ['$50K', 28],
              ['$100K', 45], ['$200K', 62], ['$500K', 80], ['$1M+', 100],
            ].map(([val, w]) => (
              <div key={val} className="scale-row">
                <span className="scale-lbl">{val}</span>
                <div className="scale-track">
                  <motion.div
                    className="scale-fill"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${w}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                  />
                </div>
              </div>
            ))}
            <p className="scale-note">Account scaling milestones</p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ─── Testimonials ─────────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  { name: 'Alex M.',  tag: '$50K Funded',  stars: 5, text: 'Passed the 2-step in 18 days. The rules are strict but fair. Payout arrived within 12 hours — exactly as advertised.' },
  { name: 'Priya S.', tag: '$100K Funded', stars: 5, text: 'Hit my profit target on day 12. The dashboard is clean, metrics are transparent, and support is very responsive.' },
  { name: 'Jonas K.', tag: '$25K Funded',  stars: 5, text: 'Coming from forex prop firms, AlphaVault is clearly built by traders who understand crypto markets deeply.' },
  { name: 'Diana R.', tag: '$200K Funded', stars: 4, text: 'The 1-step challenge is perfect for experienced traders. Clean UI, fast support, fast payouts. No hidden surprises.' },
];
function Testimonials() {
  return (
    <section className="testimonials-section">
      <FadeIn>
        <div className="section-eyebrow">Reviews</div>
        <h2 className="section-h2">What Our Traders Say</h2>
      </FadeIn>
      <div className="testi-grid">
        {TESTIMONIALS.map((t, i) => (
          <FadeIn key={t.name} delay={i * 0.1}>
            <motion.div className="testi-card" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <div className="testi-stars">{'★'.repeat(t.stars)}{'☆'.repeat(5 - t.stars)}</div>
              <p>"{t.text}"</p>
              <div className="testi-footer">
                <div className="testi-avatar">{t.name[0]}</div>
                <div>
                  <div className="testi-name">{t.name}</div>
                  <div className="testi-tag">{t.tag}</div>
                </div>
              </div>
            </motion.div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

/* ─── FAQ ──────────────────────────────────────────────────────────────────── */
const FAQS = [
  { q: 'What is AlphaVault?',                    a: 'AlphaVault is a crypto proprietary trading platform. Traders complete a performance challenge and upon passing receive access to a funded trading account with up to 90% profit split.' },
  { q: 'What is the difference between 1-Step and 2-Step?', a: '1-Step requires a single phase with 10% profit target and 6% drawdown. 2-Step has two phases (10% then 5% target) with 10% drawdown — more room to trade at a lower cost.' },
  { q: 'Is the challenge fee refundable?',        a: 'Yes. The challenge fee is fully refunded with your first approved payout. You effectively trade for free once you pass.' },
  { q: 'How and when are payouts processed?',     a: 'Payouts are processed in USDT or USDC within 12–24 hours of approval. Minimum payout is $100. Your split starts at 70% and grows to 90% with consistent performance.' },
  { q: 'What trading platforms are supported?',   a: 'AlphaVault connects directly to Bybit. You can also use the built-in AlphaVault terminal or connect via API for automated strategies.' },
  { q: 'Are bots and automated strategies allowed?', a: 'Yes, fully supported. Use any trading strategy — bots, EAs, manual, scalping, swing trading. No restrictions on style.' },
  { q: 'Can I try for free?',                     a: 'Yes. Register and select the Free Trial for a $10K funded account with a 10-day evaluation period. No payment required.' },
];
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section className="faq-section" id="faq">
      <FadeIn>
        <div className="section-eyebrow">Help</div>
        <h2 className="section-h2">Frequently Asked Questions</h2>
      </FadeIn>
      <div className="faq-list">
        {FAQS.map((f, i) => (
          <FadeIn key={f.q} delay={i * 0.05}>
            <div className={`faq-item ${open === i ? 'faq-open' : ''}`} onClick={() => setOpen(open === i ? null : i)}>
              <div className="faq-q">
                <span>{f.q}</span>
                <motion.span
                  className="faq-icon"
                  animate={{ rotate: open === i ? 45 : 0 }}
                  transition={{ duration: 0.2 }}
                >+</motion.span>
              </div>
              <AnimatePresence>
                {open === i && (
                  <motion.div
                    className="faq-a"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="faq-a-inner">{f.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

/* ─── Bot Banner ───────────────────────────────────────────────────────────── */
const BOT_PREVIEW = [
  { name: 'BTC Accumulation', tag: 'FREE', freq: 'Weekly · DCA', risk: 'Low', riskColor: '#10b981', ret: '+4.2%', icon: '₿', color: '#f59e0b' },
  { name: 'RSI Momentum Pro', tag: 'CHALLENGE', freq: 'Intraday · Momentum', risk: 'Medium', riskColor: '#f59e0b', ret: '+8.3%', icon: '⚡', color: '#06b6d4' },
  { name: 'Breakout Hunter',  tag: 'CHALLENGE', freq: 'Swing · Breakout', risk: 'Medium', riskColor: '#f59e0b', ret: '+9.7%', icon: '🎯', color: '#10b981' },
];

function BotBanner() {
  return (
    <section className="bot-banner-section">
      <div className="bot-banner-glow" />
      <div className="bot-banner-inner">
        <FadeIn className="bot-banner-left">
          <div className="section-eyebrow">Automated Trading</div>
          <h2 className="section-h2">
            AI Bots That Trade<br />
            <span style={{ background: 'linear-gradient(135deg,#7c3aed,#06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              On Your Behalf
            </span>
          </h2>
          <p className="section-sub" style={{ marginBottom: 28 }}>
            Deploy institutional-grade strategies from day one.
            Free DCA bots available to all — challenge bots unlock when you get funded.
          </p>
          <div className="bot-banner-badges">
            <div className="bot-banner-badge">
              <span style={{ color: '#10b981', fontSize: 18 }}>🎁</span>
              <div>
                <strong>2 Free Bots</strong>
                <span>No challenge required</span>
              </div>
            </div>
            <div className="bot-banner-badge">
              <span style={{ color: '#a78bfa', fontSize: 18 }}>⚡</span>
              <div>
                <strong>4 Pro Bots</strong>
                <span>Unlock with any challenge</span>
              </div>
            </div>
          </div>
          <Link to="/bots" className="btn btn-primary" style={{ marginTop: 28, display: 'inline-flex' }}>
            Access Trading Bots →
          </Link>
        </FadeIn>

        <FadeIn delay={0.15} className="bot-banner-cards">
          {BOT_PREVIEW.map((bot, i) => (
            <motion.div
              key={bot.name}
              className="bot-preview-card"
              style={{ '--bc': bot.color }}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="bot-preview-top">
                <div className="bot-preview-icon" style={{ background: `${bot.color}1a`, color: bot.color }}>
                  {bot.icon}
                </div>
                <div className="bot-preview-info">
                  <div className="bot-preview-name">{bot.name}</div>
                  <div className="bot-preview-freq">{bot.freq}</div>
                </div>
                <span className={`bot-preview-tag ${bot.tag === 'FREE' ? 'free' : 'pro'}`}>{bot.tag}</span>
              </div>
              <div className="bot-preview-row">
                <span className="bot-preview-label">Risk</span>
                <span className="bot-preview-val" style={{ color: bot.riskColor }}>{bot.risk}</span>
                <span className="bot-preview-label" style={{ marginLeft: 'auto' }}>Avg Return</span>
                <span className="bot-preview-val" style={{ color: bot.color }}>{bot.ret}</span>
              </div>
            </motion.div>
          ))}
          <div className="bot-preview-more">
            <Link to="/bots">+ 3 more bots in the platform →</Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ─── Final CTA ────────────────────────────────────────────────────────────── */
function FinalCTA() {
  return (
    <section className="cta-section">
      <div className="cta-glow" />
      <FadeIn>
        <div className="section-eyebrow">Ready?</div>
        <h2 className="cta-h2">Start Your Challenge Today</h2>
        <p className="cta-sub">Join 12,400+ funded traders. Start free — no payment required.</p>
        <div className="cta-btns">
          <Link to="/register" className="btn btn-primary btn-lg">Get Started →</Link>
          <Link to="/pricing"  className="btn btn-outline btn-lg">Compare Plans</Link>
        </div>
        <div className="cta-trust-row">
          <span>✓ Fee refunded on first payout</span>
          <span>✓ Payouts in 12–24 hours</span>
          <span>✓ Up to 90% profit split</span>
          <span>✓ 700+ trading pairs</span>
        </div>
      </FadeIn>
    </section>
  );
}

/* ─── Footer ───────────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="av-footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <div className="footer-logo">▲ AlphaVault</div>
          <p>Crypto Prop Trading Platform</p>
          <p>Funded accounts · USDT payouts · 700+ pairs</p>
        </div>
        <div className="footer-links">
          <div className="fl-col">
            <span className="fl-title">Platform</span>
            <Link to="/pricing">Challenges</Link>
            <a href="#how">How It Works</a>
            <a href="#platforms">Integrations</a>
          </div>
          <div className="fl-col">
            <span className="fl-title">Account</span>
            <Link to="/register">Register</Link>
            <Link to="/login">Login</Link>
            <Link to="/register">Free Trial</Link>
          </div>
          <div className="fl-col">
            <span className="fl-title">Support</span>
            <a href="#faq">FAQ</a>
            <a href="mailto:support@alphavault.live">Contact</a>
          </div>
        </div>
      </div>
      <div className="footer-disclaimer">
        <p>
          All information provided on this site is intended solely for educational purposes related to
          trading on financial markets and does not serve in any way as a specific investment
          recommendation, business recommendation, investment opportunity analysis or similar general
          recommendation regarding the trading of investment instruments. Alpha Vault Funding only
          provides services of simulated trading and educational tools for traders. The information on
          this site is not directed at residents in any country or jurisdiction where such distribution
          or use would be contrary to local laws or regulations. Alpha Vault Funding companies do not
          act as a broker and do not accept any deposits. The offered technical solution for the Alpha
          Vault Funding platforms and data feed is powered by liquidity providers.
        </p>
        <p className="footer-disclaimer-contact">
          For inquiries contact us at:{' '}
          <a href="mailto:support@alphavault.live">support@alphavault.live</a>
        </p>
      </div>

      <div className="footer-bottom">
        <span>© 2025 Alpha Vault Funding · Crypto Prop Trading</span>
        <div className="footer-bottom-links">
          <a href="#">Privacy Policy</a>
          <a href="#">Terms of Service</a>
          <a href="#">Risk Disclaimer</a>
        </div>
      </div>
    </footer>
  );
}

/* ─── Main export ──────────────────────────────────────────────────────────── */
export default function Landing() {
  return (
    <div className="landing-page">
      <Navbar />
      <PayoutTicker />
      <Hero />
      <StatsBar />
      <HowItWorks />
      <PricingSection />
      <Features />
      <PlatformPreview />
      <PayoutTiers />
      <BotBanner />
      <Testimonials />
      <FinalCTA />
      <FAQ />
      <Footer />
    </div>
  );
}
